import React, { useState, useEffect } from "react";
import { database } from "../../firebaseConfig";
import { ref, get, set, push } from "firebase/database";
import "./OfferMaker.css";
import "./ContractViewer.css";

// Helper functions from Contracts.js
const formatAmount = (amount, currency = 'EUR', includeWords = true, language = 'de') => {
  const locale = language === 'de' ? 'de-DE' : 'en-US';
  const formattedAmount = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);

  if (!includeWords) {
    return formattedAmount;
  }

  // Simple number to words conversion
  const numberToGermanWords = (num) => {
    const ones = ['', 'eins', 'zwei', 'drei', 'vier', 'fünf', 'sechs', 'sieben', 'acht', 'neun'];
    const tens = ['', '', 'zwanzig', 'dreißig', 'vierzig', 'fünfzig', 'sechzig', 'siebzig', 'achtzig', 'neunzig'];
    const teens = ['zehn', 'elf', 'zwölf', 'dreizehn', 'vierzehn', 'fünfzehn', 'sechzehn', 'siebzehn', 'achtzehn', 'neunzehn'];
    
    if (num === 0) return 'null';
    if (num < 0) return 'minus ' + numberToGermanWords(-num);
    
    let result = '';
    
    if (num >= 1000) {
      const thousandsPart = Math.floor(num / 1000);
      if (thousandsPart === 1) {
        result += 'ein';
      } else {
        result += numberToGermanWords(thousandsPart);
      }
      result += 'tausend';
      num = num % 1000;
      if (num > 0) result += 'und';
    }
    
    if (num >= 100) {
      const hundredsPart = Math.floor(num / 100);
      if (hundredsPart === 1) {
        result += 'ein';
      } else {
        result += ones[hundredsPart];
      }
      result += 'hundert';
      num = num % 100;
      if (num > 0) result += 'und';
    }
    
    if (num >= 20) {
      const tensPart = Math.floor(num / 10);
      const onesPart = num % 10;
      if (onesPart > 0) {
        result += ones[onesPart] + 'und';
      }
      result += tens[tensPart];
    } else if (num >= 10) {
      result += teens[num - 10];
    } else if (num > 0) {
      result += ones[num];
    }
    
    return result;
  };

  if (language === 'de') {
    const amountWords = numberToGermanWords(Math.floor(amount));
    const currencyWord = currency === 'EUR' ? 'Euro' : currency;
    // Don't add 's' to German number words - "Euro" has no plural form in German
    // "tausend" should never get an 's' suffix
    return `${formattedAmount} (${amountWords} ${currencyWord})`;
  } else {
    // English number to words (simplified)
    const numberToEnglishWords = (num) => {
      const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
      const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
      const teens = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
      
      if (num === 0) return 'zero';
      if (num < 0) return 'minus ' + numberToEnglishWords(-num);
      
      let result = '';
      
      if (num >= 1000) {
        const thousandsPart = Math.floor(num / 1000);
        result += numberToEnglishWords(thousandsPart) + ' thousand';
        num = num % 1000;
        if (num > 0) result += ' ';
      }
      
      if (num >= 100) {
        const hundredsPart = Math.floor(num / 100);
        result += ones[hundredsPart] + ' hundred';
        num = num % 100;
        if (num > 0) result += ' ';
      }
      
      if (num >= 20) {
        const tensPart = Math.floor(num / 10);
        const onesPart = num % 10;
        result += tens[tensPart];
        if (onesPart > 0) {
          result += '-' + ones[onesPart];
        }
      } else if (num >= 10) {
        result += teens[num - 10];
      } else if (num > 0) {
        result += ones[num];
      }
      
      return result;
    };
    
    const amountWords = numberToEnglishWords(Math.floor(amount));
    const currencyWord = currency === 'EUR' ? 'euros' : currency;
    return `${formattedAmount} (${amountWords} ${currencyWord})`;
  }
};

const formatDate = (dateValue, format = 'full', language = 'de') => {
  if (!dateValue) return '';
  
  let date;
  if (typeof dateValue === 'string') {
    if (dateValue.includes('T')) {
      date = new Date(dateValue);
    } else if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      date = new Date(dateValue + 'T00:00:00');
    } else {
      date = new Date(dateValue);
    }
  } else if (typeof dateValue === 'number') {
    date = new Date(dateValue);
  } else {
    date = dateValue;
  }

  if (isNaN(date.getTime())) {
    return '';
  }

  const locale = language === 'de' ? 'de-DE' : 'en-US';
  
  if (format === 'full') {
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  } else if (format === 'short') {
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } else {
    return date.toLocaleDateString(locale);
  }
};

const formatDuration = (value, unit = 'months', language = 'de') => {
  const num = parseInt(value);
  if (isNaN(num)) return '';

  if (language === 'de') {
    if (unit === 'months' || unit === 'Monate' || unit === 'month') {
      if (num === 1) return '1 Monat';
      if (num === 12) return '1 Jahr';
      if (num > 12 && num % 12 === 0) {
        const years = num / 12;
        return years === 1 ? '1 Jahr' : `${years} Jahre`;
      }
      return `${num} Monate`;
    } else if (unit === 'years' || unit === 'Jahre' || unit === 'year' || unit === 'Jahr') {
      if (num === 1) return '1 Jahr';
      return `${num} Jahre`;
    } else if (unit === 'days' || unit === 'Tage' || unit === 'day' || unit === 'Tag') {
      if (num === 1) return '1 Tag';
      return `${num} Tage`;
    } else if (unit === 'weeks' || unit === 'Wochen' || unit === 'week' || unit === 'Woche') {
      if (num === 1) return '1 Woche';
      return `${num} Wochen`;
    }
  } else {
    // English
    if (unit === 'months' || unit === 'Monate' || unit === 'month') {
      if (num === 1) return '1 month';
      if (num === 12) return '1 year';
      if (num > 12 && num % 12 === 0) {
        const years = num / 12;
        return years === 1 ? '1 year' : `${years} years`;
      }
      return `${num} months`;
    } else if (unit === 'years' || unit === 'Jahre' || unit === 'year' || unit === 'Jahr') {
      if (num === 1) return '1 year';
      return `${num} years`;
    } else if (unit === 'days' || unit === 'Tage' || unit === 'day' || unit === 'Tag') {
      if (num === 1) return '1 day';
      return `${num} days`;
    } else if (unit === 'weeks' || unit === 'Wochen' || unit === 'week' || unit === 'Woche') {
      if (num === 1) return '1 week';
      return `${num} weeks`;
    }
  }

  return `${num} ${unit}`;
};

// Extract variables from text
const extractVariables = (text) => {
  const variablePattern = /\{\{(\w+)\}\}/g;
  const variables = [];
  let match;
  const seen = new Set();
  
  while ((match = variablePattern.exec(text)) !== null) {
    const varName = match[1];
    
    if (seen.has(varName)) continue;
    seen.add(varName);
    
    const varNameLower = varName.toLowerCase();
    
    let variable = {
      name: varName,
      fullMatch: match[0],
      type: 'text'
    };

    if (varNameLower.includes('amount') || varNameLower.includes('fee') || varNameLower.includes('price') || 
        varNameLower.includes('summe') || varNameLower.includes('betrag') || varNameLower.includes('kosten')) {
      variable.type = 'amount';
    } else if (varNameLower.includes('date') || varNameLower.includes('datum') || varNameLower.includes('start') || 
               varNameLower.includes('ende') || varNameLower.includes('end') || varNameLower.includes('zeitpunkt')) {
      variable.type = 'date';
    } else if (varNameLower.includes('duration') || varNameLower.includes('dauer') || varNameLower.includes('laufzeit')) {
      variable.type = 'duration';
    }

    variables.push(variable);
  }
  return variables;
};

// Replace variables in text with actual values
const replaceVariables = (text, variableValues, language = 'de') => {
  const variablePattern = /\{\{(\w+)\}\}/g;
  return text.replace(variablePattern, (match, varName) => {
    const value = variableValues[varName];
    if (value === undefined || value === null || value === '') {
      return `[${varName}]`;
    }
    
    const varNameLower = varName.toLowerCase();
    
    // Format based on type
    if (varNameLower.includes('amount') || varNameLower.includes('fee') || varNameLower.includes('price') || 
        varNameLower.includes('summe') || varNameLower.includes('betrag') || varNameLower.includes('kosten')) {
      return formatAmount(parseFloat(value), 'EUR', true, language);
    } else if (varNameLower.includes('date') || varNameLower.includes('datum') || varNameLower.includes('start') || 
               varNameLower.includes('ende') || varNameLower.includes('end') || varNameLower.includes('zeitpunkt')) {
      return formatDate(value, 'full', language);
    } else if (varNameLower.includes('duration') || varNameLower.includes('dauer') || varNameLower.includes('laufzeit')) {
      // Assume duration is in months if not specified
      return formatDuration(parseInt(value), 'months', language);
    }
    
    return value;
  });
};

const ContractMaker = ({ userId, initialContractData = null, isEditMode = false, onContractSaved = () => {}, onlyPreview = false }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [contractData, setContractData] = useState({
    templateId: "",
    customerName: "",
    customerAddress: "",
    customerStreet: "",
    customerZip: "",
    customerCity: "",
    customerCountry: "",
    customerRepresentedBy: "",
    contractDate: new Date().toISOString().split('T')[0],
    contractLocation: "",
    contractAdditionalText: "",
    variableValues: {},
    teamMemberId: "",
    ourSignature: "",
    customerSignature: "",
    language: "de", // "de" or "en"
    showManagedBy: true, // Toggle for "Managed by" display
    createdAt: Date.now()
  });
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [contractId, setContractId] = useState(null);

  // Load templates and team members
  useEffect(() => {
    const loadData = async () => {
      if (!userId) return;

      try {
        // Load templates
        const templatesRef = ref(database, `/World/${userId}/contractTemplates`);
        const templatesSnapshot = await get(templatesRef);
        
        if (templatesSnapshot.exists()) {
          const templatesList = [];
          templatesSnapshot.forEach((childSnapshot) => {
            templatesList.push({
              id: childSnapshot.key,
              ...childSnapshot.val()
            });
          });
          setTemplates(templatesList);
        }

        // Load team members
        const teamRef = ref(database, `/World/${userId}/team/members`);
        const teamSnapshot = await get(teamRef);
        
        if (teamSnapshot.exists()) {
          const members = [];
          teamSnapshot.forEach((childSnapshot) => {
            members.push({
              id: childSnapshot.key,
              ...childSnapshot.val()
            });
          });
          setTeamMembers(members);
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error loading data:", error);
        showNotification("Error loading data", "error");
        setIsLoading(false);
      }
    };

    loadData();
  }, [userId]);

  // Initialize contract data
  useEffect(() => {
    if (initialContractData) {
      setContractId(initialContractData.id || null);
      setContractData({
        templateId: initialContractData.templateId || "",
        customerName: initialContractData.customerName || "",
        customerAddress: initialContractData.customerAddress || "",
        customerStreet: initialContractData.customerStreet || "",
        customerZip: initialContractData.customerZip || "",
        customerCity: initialContractData.customerCity || "",
        customerCountry: initialContractData.customerCountry || "",
        customerRepresentedBy: initialContractData.customerRepresentedBy || "",
        contractDate: initialContractData.contractDate || new Date().toISOString().split('T')[0],
        contractLocation: initialContractData.contractLocation || "",
        contractAdditionalText: initialContractData.contractAdditionalText || "",
        variableValues: initialContractData.variableValues || {},
        teamMemberId: initialContractData.teamMemberId || "",
        ourSignature: initialContractData.ourSignature || "",
        customerSignature: initialContractData.customerSignature || "",
        language: initialContractData.language || "de",
        showManagedBy: initialContractData.showManagedBy !== undefined ? initialContractData.showManagedBy : true,
        createdAt: initialContractData.createdAt || Date.now()
      });
      setSelectedTemplateId(initialContractData.templateId || "");
      
      if (initialContractData.teamMemberId) {
        const member = teamMembers.find(m => m.id === initialContractData.teamMemberId);
        if (member) {
          setSelectedTeamMember(member);
        }
      }
    }
  }, [initialContractData, teamMembers]);

  // Load template when selected
  useEffect(() => {
    if (selectedTemplateId && templates.length > 0) {
      const template = templates.find(t => t.id === selectedTemplateId);
      if (template) {
        setSelectedTemplate(template);
        setContractData(prev => ({ ...prev, templateId: selectedTemplateId }));
      }
    }
  }, [selectedTemplateId, templates]);

  // Get all variables from selected template
  const getAllVariables = () => {
    if (!selectedTemplate || !selectedTemplate.paragraphs) return [];
    
    const allVars = new Set();
    selectedTemplate.paragraphs.forEach(para => {
      const vars = extractVariables(para.text);
      vars.forEach(v => allVars.add(v.name));
    });
    
    return Array.from(allVars).map(varName => {
      const varNameLower = varName.toLowerCase();
      let type = 'text';
      if (varNameLower.includes('amount') || varNameLower.includes('fee') || varNameLower.includes('price') || 
          varNameLower.includes('summe') || varNameLower.includes('betrag') || varNameLower.includes('kosten')) {
        type = 'amount';
      } else if (varNameLower.includes('date') || varNameLower.includes('datum') || varNameLower.includes('start') || 
                 varNameLower.includes('ende') || varNameLower.includes('end') || varNameLower.includes('zeitpunkt')) {
        type = 'date';
      } else if (varNameLower.includes('duration') || varNameLower.includes('dauer') || varNameLower.includes('laufzeit')) {
        type = 'duration';
      }
      return { name: varName, type };
    });
  };

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplateId(templateId);
    // Reset variable values when template changes
    setContractData(prev => ({ ...prev, variableValues: {} }));
  };

  const handleVariableChange = (varName, value) => {
    setContractData(prev => ({
      ...prev,
      variableValues: {
        ...prev.variableValues,
        [varName]: value
      }
    }));
  };

  const handleTeamMemberChange = (e) => {
    const memberId = e.target.value;
    const member = teamMembers.find(m => m.id === memberId);
    setSelectedTeamMember(member);
    setContractData(prev => ({ ...prev, teamMemberId: memberId }));
  };

  const handleSave = async () => {
    if (!userId) return;

    if (!selectedTemplateId) {
      showNotification("Please select a template", "error");
      return;
    }

    if (!contractData.customerName.trim()) {
      showNotification("Please enter customer name", "error");
      return;
    }

    try {
      const contractRef = contractId 
        ? ref(database, `/World/${userId}/contracts/${contractId}`)
        : ref(database, `/World/${userId}/contracts`);
      
      const contractToSave = {
        ...contractData,
        updatedAt: Date.now()
      };

      if (contractId) {
        await set(ref(database, `/World/${userId}/contracts/${contractId}`), contractToSave);
      } else {
        const newContractRef = push(contractRef);
        await set(newContractRef, {
          ...contractToSave,
          createdAt: Date.now()
        });
        setContractId(newContractRef.key);
      }

      showNotification("Contract saved successfully", "success");
      onContractSaved();
    } catch (error) {
      console.error("Error saving contract:", error);
      showNotification("Error saving contract", "error");
    }
  };

  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification({ message: "", type: "" });
    }, 3000);
  };

  const handleExport = () => {
    if (!selectedTemplate) {
      showNotification("Please select a template first", "error");
      return;
    }

    // Create a new window with the contract content
    const printWindow = window.open('', '_blank');
    
    const contractHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${selectedTemplate.name}</title>
        <style>
          @media print {
            @page {
              margin: 2.5cm 2cm;
            }
          }
          body {
            font-family: 'Arial', sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 750px;
            margin: 0 auto;
            padding: 40px 30px;
          }
          @media print {
            body {
              padding: 30px 20px;
            }
          }
          h1 {
            text-align: center;
            margin-top: 30px;
            margin-bottom: 1.5rem;
            color: #1a202c;
          }
          h3 {
            margin-top: 1.5rem;
            margin-bottom: 0.75rem;
            font-weight: 600;
            color: #2d3748;
            padding-right: 10px;
          }
          .paragraph {
            margin-bottom: 1.5rem;
            white-space: pre-wrap;
            padding: 0 10px;
          }
          .paragraph div {
            padding-right: 5px;
          }
          .signatures {
            margin-top: 3rem;
            display: flex;
            justify-content: space-between;
            border-top: 1px solid #ddd;
            padding-top: 2rem;
          }
          .signature-box {
            flex: 1;
            text-align: center;
            padding: 0 20px;
          }
          .signature-image {
            border-radius: 4px;
            padding: 1rem;
            height: 120px;
            margin-bottom: 0.5rem;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }
          .signature-image img {
            max-width: 200px;
            max-height: 100px;
          }
          .signature-name {
            font-weight: 600;
            margin-top: 0.5rem;
          }
          .signature-company {
            font-size: 0.9em;
            color: #666;
          }
          .customer-info {
            margin-bottom: 2rem;
            padding: 1rem;
            background-color: #f9f9f9;
            border-radius: 4px;
          }
        </style>
      </head>
      <body>
        <h1>${selectedTemplate.name}</h1>
        
        ${contractData.contractDate ? `<div style="text-align: center; margin-bottom: 0.5rem; font-size: 0.9em; color: #666;">${formatDate(contractData.contractDate, 'full', contractData.language)}</div>` : ''}
        ${contractData.contractLocation ? `<div style="text-align: center; margin-bottom: 2rem; font-size: 0.9em; color: #666;">${contractData.contractLocation}</div>` : ''}
        ${!contractData.contractLocation && contractData.contractDate ? `<div style="margin-bottom: 2rem;"></div>` : ''}
        
        <!-- Parties Section -->
        <div style="margin-bottom: 2rem; padding: 1.5rem 1rem; border-radius: 4px;">
          <!-- Anbieter -->
          <div style="margin-bottom: 1.5rem;">
            <div>
              <div style="font-weight: 600; margin-bottom: 0.25rem;">${selectedTeamMember?.companyName || 'Our Company'}</div>
              ${contractData.showManagedBy && selectedTeamMember?.name ? `<div style="font-size: 0.9em; margin-bottom: 0.25rem; color: #666;">Vertreten durch: ${selectedTeamMember.name}</div>` : ''}
              ${selectedTeamMember?.billingStreet ? `<div>${selectedTeamMember.billingStreet}</div>` : ''}
              ${selectedTeamMember?.billingZip && selectedTeamMember?.billingCity ? `<div>${selectedTeamMember.billingZip} ${selectedTeamMember.billingCity}</div>` : ''}
              ${selectedTeamMember?.billingCountry ? `<div>${selectedTeamMember.billingCountry}</div>` : ''}
              <div style="font-size: 0.9em; color: #666; font-style: italic; margin-top: 0.5rem;">
                - Nachstehend als <strong>Anbieter</strong> bezeichnet
              </div>
            </div>
          </div>
          
          <div style="text-align: left; margin: 1rem 0; font-weight: 600;">Und</div>
          
          <!-- Auftraggeber -->
          <div>
            <div>
              <div style="font-weight: 600; margin-bottom: 0.25rem;">${contractData.customerName || 'Customer Name'}</div>
              ${contractData.customerRepresentedBy ? `<div style="font-size: 0.9em; margin-bottom: 0.25rem;">Vertreten durch: ${contractData.customerRepresentedBy}</div>` : ''}
              ${contractData.customerStreet ? `<div>${contractData.customerStreet}</div>` : ''}
              ${contractData.customerZip && contractData.customerCity ? `<div>${contractData.customerZip} ${contractData.customerCity}</div>` : ''}
              ${contractData.customerCountry ? `<div>${contractData.customerCountry}</div>` : ''}
              <div style="font-size: 0.9em; color: #666; font-style: italic; margin-top: 0.5rem;">
                - Nachstehend als <strong>Auftraggeber</strong> bezeichnet
              </div>
            </div>
          </div>
        </div>

        ${selectedTemplate.paragraphs.map((para, index) => `
          <div class="paragraph">
            <h3>§${para.order || index + 1} ${para.title}</h3>
            <div>${replaceVariables(para.text, contractData.variableValues, contractData.language).replace(/\n/g, '<br>')}</div>
          </div>
        `).join('')}

        ${contractData.contractAdditionalText ? `
          <div style="margin-top: 2rem; margin-bottom: 2rem; padding: 1rem 1.5rem; white-space: pre-wrap; line-height: 1.6; color: #333;">
            ${contractData.contractAdditionalText.replace(/\n/g, '<br>')}
          </div>
        ` : ''}

        <div class="signatures">
          <div class="signature-box">
            <div class="signature-image">
              ${selectedTeamMember?.signature ? 
                `<img src="${selectedTeamMember.signature}" alt="Our signature" style="max-width: ${selectedTeamMember.signatureMaxWidth || 150}px; max-height: 100px;" />` : 
                '<div style="color: #999;">Our Signature</div>'
              }
            </div>
            <div class="signature-name">${selectedTeamMember?.companyName || 'Our Company'}</div>
            <div class="signature-company">${selectedTeamMember?.billingCity || ''}</div>
          </div>
          <div class="signature-box">
            <div class="signature-image">
              ${contractData.customerSignature ? 
                `<img src="${contractData.customerSignature}" alt="Customer signature" style="max-width: 200px; max-height: 100px;" />` : 
                ''
              }
            </div>
            <div class="signature-name">${contractData.customerName || 'Customer Name'}</div>
            <div class="signature-company">${contractData.customerCity || ''}</div>
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(contractHTML);
    printWindow.document.close();
    
    // Wait for images to load, then print
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  if (isLoading) {
    return (
      <div className="offer-maker__loading">
        <div className="offer-maker__spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const allVariables = getAllVariables();

  // If onlyPreview is true, show only the contract preview without edit fields
  if (onlyPreview) {
    return (
      <div className="contract-viewer contract-viewer--preview-only">
        {selectedTemplate && selectedTemplate.paragraphs && (
          <div className="contract-viewer__container">
            <h2 className="contract-viewer__title">
              {selectedTemplate.name}
            </h2>
            
            {contractData.contractDate && (
              <div className="contract-viewer__date">
                {formatDate(contractData.contractDate, 'full', contractData.language)}
              </div>
            )}
            {contractData.contractLocation && (
              <div className="contract-viewer__location">
                {contractData.contractLocation}
              </div>
            )}
            {!contractData.contractLocation && contractData.contractDate && (
              <div className="contract-viewer__spacer"></div>
            )}

            {/* Parties Section */}
            <div className="contract-viewer__parties">
              {/* Anbieter (Provider) */}
              <div className="contract-viewer__party contract-viewer__party--provider">
                <div className="contract-viewer__party-content">
                  <div className="contract-viewer__party-name">
                    {selectedTeamMember?.companyName || 'Our Company'}
                  </div>
                  {contractData.showManagedBy && selectedTeamMember?.name && (
                    <div className="contract-viewer__party-represented">
                      Vertreten durch: {selectedTeamMember.name}
                    </div>
                  )}
                  {selectedTeamMember?.billingStreet && (
                    <div className="contract-viewer__party-address">{selectedTeamMember.billingStreet}</div>
                  )}
                  {selectedTeamMember?.billingZip && selectedTeamMember?.billingCity && (
                    <div className="contract-viewer__party-address">{selectedTeamMember.billingZip} {selectedTeamMember.billingCity}</div>
                  )}
                  {selectedTeamMember?.billingCountry && (
                    <div className="contract-viewer__party-address">{selectedTeamMember.billingCountry}</div>
                  )}
                  <div className="contract-viewer__party-label">
                    - Nachstehend als <strong>Anbieter</strong> bezeichnet
                  </div>
                </div>
              </div>

              {/* Separator */}
              <div className="contract-viewer__separator">
                Und
              </div>

              {/* Auftraggeber (Client) */}
              <div className="contract-viewer__party contract-viewer__party--client">
                <div className="contract-viewer__party-content">
                  <div className="contract-viewer__party-name">
                    {contractData.customerName || 'Customer Name'}
                  </div>
                  {contractData.customerRepresentedBy && (
                    <div className="contract-viewer__party-represented">
                      Vertreten durch: {contractData.customerRepresentedBy}
                    </div>
                  )}
                  {contractData.customerStreet && (
                    <div className="contract-viewer__party-address">{contractData.customerStreet}</div>
                  )}
                  {contractData.customerZip && contractData.customerCity && (
                    <div className="contract-viewer__party-address">{contractData.customerZip} {contractData.customerCity}</div>
                  )}
                  {contractData.customerCountry && (
                    <div className="contract-viewer__party-address">{contractData.customerCountry}</div>
                  )}
                  <div className="contract-viewer__party-label">
                    - Nachstehend als <strong>Auftraggeber</strong> bezeichnet
                  </div>
                </div>
              </div>
            </div>
          
            {selectedTemplate.paragraphs.map((para, index) => (
              <div key={index} className="contract-viewer__paragraph">
                <h3 className="contract-viewer__paragraph-title">
                  §{para.order || index + 1} {para.title}
                </h3>
                <div className="contract-viewer__paragraph-content">
                  {replaceVariables(para.text, contractData.variableValues, contractData.language)}
                </div>
              </div>
            ))}

            {/* Additional Text */}
            {contractData.contractAdditionalText && (
              <div className="contract-viewer__additional-text">
                {contractData.contractAdditionalText}
              </div>
            )}

            {/* Signatures */}
            <div className="contract-viewer__signatures">
              <div className="contract-viewer__signature contract-viewer__signature--provider">
                <div className="contract-viewer__signature-box">
                  {selectedTeamMember?.signature ? (
                    <img 
                      src={selectedTeamMember.signature} 
                      alt="Our signature" 
                      className="contract-viewer__signature-image"
                      style={{ maxWidth: `${selectedTeamMember.signatureMaxWidth || 150}px`, maxHeight: '100px' }}
                    />
                  ) : null}
                </div>
                <div className="contract-viewer__signature-name">
                  {selectedTeamMember?.companyName || 'Our Company'}
                </div>
                <div className="contract-viewer__signature-city">
                  {selectedTeamMember?.billingCity || ''}
                </div>
              </div>
              <div className="contract-viewer__signature contract-viewer__signature--client">
                <div className="contract-viewer__signature-box">
                  {contractData.customerSignature ? (
                    <img 
                      src={contractData.customerSignature} 
                      alt="Customer signature" 
                      className="contract-viewer__signature-image"
                      style={{ maxWidth: '200px', maxHeight: '100px' }}
                    />
                  ) : null}
                </div>
                <div className="contract-viewer__signature-name">
                  {contractData.customerName || 'Customer Name'}
                </div>
                <div className="contract-viewer__signature-city">
                  {contractData.customerCity || ''}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="offer-maker__editor">
      {notification.message && (
        <div className={`offer-maker__notification offer-maker__notification--${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start' }}>
        {/* Left Column - Form Data */}
        <div style={{ flex: '0 0 400px' }}>
          <div className="offer-maker__section" style={{ marginBottom: 0 }}>
        <div className="offer-maker__section-header">
          <span className="offer-maker__section-icon">📄</span>
          <h3 className="offer-maker__section-title">Create Contract</h3>
        </div>

        {/* Template Selection */}
        <div className="offer-maker__input-group">
          <label className="offer-maker__label">
            Contract Template <span className="offer-maker__required">*</span>
          </label>
          <select
            value={selectedTemplateId}
            onChange={handleTemplateChange}
            className="offer-maker__input"
          >
            <option value="">Select a template</option>
            {templates.map(template => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">Contract Date</label>
          <input
            type="date"
            value={contractData.contractDate}
            onChange={(e) => setContractData(prev => ({ ...prev, contractDate: e.target.value }))}
            className="offer-maker__input"
          />
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">Contract Ort</label>
          <input
            type="text"
            value={contractData.contractLocation}
            onChange={(e) => setContractData(prev => ({ ...prev, contractLocation: e.target.value }))}
            className="offer-maker__input"
            placeholder="e.g. München"
          />
        </div>

        {/* Customer Information */}
        <div className="offer-maker__section-header" style={{ marginTop: '2rem' }}>
          <span className="offer-maker__section-icon">👤</span>
          <h3 className="offer-maker__section-title">Customer Information</h3>
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">
            Customer Name <span className="offer-maker__required">*</span>
          </label>
          <input
            type="text"
            value={contractData.customerName}
            onChange={(e) => setContractData(prev => ({ ...prev, customerName: e.target.value }))}
            className="offer-maker__input"
            placeholder="Customer or company name"
          />
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">Street & Number</label>
          <input
            type="text"
            value={contractData.customerStreet}
            onChange={(e) => setContractData(prev => ({ ...prev, customerStreet: e.target.value }))}
            className="offer-maker__input"
            placeholder="Street and house number"
          />
        </div>

        <div className="offer-maker__input-group" style={{ display: 'flex', gap: '10px' }}>
          <div style={{ flex: '1' }}>
            <label className="offer-maker__label">ZIP Code</label>
            <input
              type="text"
              value={contractData.customerZip}
              onChange={(e) => setContractData(prev => ({ ...prev, customerZip: e.target.value }))}
              className="offer-maker__input"
              placeholder="12345"
            />
          </div>
          <div style={{ flex: '2' }}>
            <label className="offer-maker__label">City</label>
            <input
              type="text"
              value={contractData.customerCity}
              onChange={(e) => setContractData(prev => ({ ...prev, customerCity: e.target.value }))}
              className="offer-maker__input"
              placeholder="City"
            />
          </div>
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">Country</label>
          <input
            type="text"
            value={contractData.customerCountry}
            onChange={(e) => setContractData(prev => ({ ...prev, customerCountry: e.target.value }))}
            className="offer-maker__input"
            placeholder="Germany"
          />
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">Represented By (Vertreten durch)</label>
          <input
            type="text"
            value={contractData.customerRepresentedBy}
            onChange={(e) => setContractData(prev => ({ ...prev, customerRepresentedBy: e.target.value }))}
            className="offer-maker__input"
            placeholder="e.g. Peter Wölfer"
          />
        </div>

        {/* Language Selection */}
        <div className="offer-maker__input-group">
          <label className="offer-maker__label">Language for Dynamic Fields</label>
          <select
            value={contractData.language}
            onChange={(e) => setContractData(prev => ({ ...prev, language: e.target.value }))}
            className="offer-maker__input"
          >
            <option value="de">German (Deutsch)</option>
            <option value="en">English</option>
          </select>
        </div>

        {/* Variables */}
        {allVariables.length > 0 && (
          <>
            <div className="offer-maker__section-header" style={{ marginTop: '2rem' }}>
              <span className="offer-maker__section-icon">📝</span>
              <h3 className="offer-maker__section-title">Contract Variables</h3>
            </div>

            {allVariables.map(variable => (
              <div key={variable.name} className="offer-maker__input-group">
                <label className="offer-maker__label">
                  {variable.name}
                  {variable.type === 'amount' && ' (Amount in EUR)'}
                  {variable.type === 'date' && ' (Date)'}
                  {variable.type === 'duration' && ' (Duration in months)'}
                </label>
                {variable.type === 'amount' && (
                  <input
                    type="number"
                    step="0.01"
                    value={contractData.variableValues[variable.name] || ''}
                    onChange={(e) => handleVariableChange(variable.name, e.target.value)}
                    className="offer-maker__input"
                    placeholder="0.00"
                  />
                )}
                {variable.type === 'date' && (
                  <input
                    type="date"
                    value={contractData.variableValues[variable.name] || ''}
                    onChange={(e) => handleVariableChange(variable.name, e.target.value)}
                    className="offer-maker__input"
                  />
                )}
                {variable.type === 'duration' && (
                  <input
                    type="number"
                    value={contractData.variableValues[variable.name] || ''}
                    onChange={(e) => handleVariableChange(variable.name, e.target.value)}
                    className="offer-maker__input"
                    placeholder="12"
                  />
                )}
                {variable.type === 'text' && (
                  <input
                    type="text"
                    value={contractData.variableValues[variable.name] || ''}
                    onChange={(e) => handleVariableChange(variable.name, e.target.value)}
                    className="offer-maker__input"
                    placeholder={`Enter ${variable.name}`}
                  />
                )}
              </div>
            ))}
          </>
        )}

        {/* Team Member Selection */}
        <div className="offer-maker__section-header" style={{ marginTop: '2rem' }}>
          <span className="offer-maker__section-icon">👥</span>
          <h3 className="offer-maker__section-title">Our Information</h3>
        </div>

        <div className="offer-maker__team-select-container">
          <label className="offer-maker__team-select-label">Team Member</label>
          <select
            className="offer-maker__team-select"
            value={selectedTeamMember?.id || ""}
            onChange={handleTeamMemberChange}
          >
            <option value="">Select team member</option>
            {teamMembers.map(member => (
              <option key={member.id} value={member.id}>
                {member.name} - {member.companyName}
              </option>
            ))}
          </select>
        </div>

        <div className="offer-maker__input-group" style={{ marginTop: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={contractData.showManagedBy}
              onChange={(e) => setContractData(prev => ({ ...prev, showManagedBy: e.target.checked }))}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span className="offer-maker__label" style={{ margin: 0, fontWeight: 'normal' }}>
              Show "Managed by" person name
            </span>
          </label>
        </div>

        {/* Additional Text */}
        <div className="offer-maker__section-header" style={{ marginTop: '2rem' }}>
          <span className="offer-maker__section-icon">📝</span>
          <h3 className="offer-maker__section-title">Additional Text</h3>
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">Additional Text (appears below contract)</label>
          <textarea
            value={contractData.contractAdditionalText}
            onChange={(e) => setContractData(prev => ({ ...prev, contractAdditionalText: e.target.value }))}
            className="offer-maker__input"
            placeholder="Enter additional text that will appear below the contract content..."
            rows="4"
            style={{ minHeight: '100px' }}
          />
        </div>

        {/* Signatures */}
        <div className="offer-maker__section-header" style={{ marginTop: '2rem' }}>
          <span className="offer-maker__section-icon">✍️</span>
          <h3 className="offer-maker__section-title">Signatures</h3>
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">Our Signature</label>
          <div style={{ 
            border: '2px dashed #ddd', 
            borderRadius: '8px', 
            padding: '2rem', 
            textAlign: 'center',
            minHeight: '100px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f9f9f9'
          }}>
            {selectedTeamMember?.signature ? (
              <img 
                src={selectedTeamMember.signature} 
                alt="Our signature" 
                style={{ maxWidth: `${selectedTeamMember.signatureMaxWidth || 150}px`, maxHeight: '100px' }}
              />
            ) : (
              <span style={{ color: '#999' }}>Signature will appear here when team member is selected</span>
            )}
          </div>
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">Customer Signature</label>
          <div style={{ 
            border: '2px dashed #ddd', 
            borderRadius: '8px', 
            padding: '2rem', 
            textAlign: 'center',
            minHeight: '100px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f9f9f9'
          }}>
            {contractData.customerSignature ? (
              <img 
                src={contractData.customerSignature} 
                alt="Customer signature" 
                style={{ maxWidth: '200px', maxHeight: '80px' }}
              />
            ) : (
              <span style={{ color: '#999' }}>Customer signature field (for export)</span>
            )}
          </div>
        </div>
          </div>
        </div>

        {/* Right Column - Contract Preview */}
        <div className="contract-preview contract-preview--editor">
          {selectedTemplate && selectedTemplate.paragraphs && (
            <div className="contract-preview__container">
              <h2 className="contract-preview__title">
                {selectedTemplate.name}
              </h2>
              
              {contractData.contractDate && (
                <div className="contract-preview__date">
                  {formatDate(contractData.contractDate, 'full', contractData.language)}
                </div>
              )}
              {contractData.contractLocation && (
                <div className="contract-preview__location">
                  {contractData.contractLocation}
                </div>
              )}
              {!contractData.contractLocation && contractData.contractDate && (
                <div className="contract-preview__spacer"></div>
              )}

              {/* Parties Section */}
              <div className="contract-preview__parties">
                {/* Anbieter (Provider) */}
                <div className="contract-preview__party contract-preview__party--provider">
                  <div className="contract-preview__party-content">
                    <div className="contract-preview__party-name">
                      {selectedTeamMember?.companyName || 'Our Company'}
                    </div>
                    {contractData.showManagedBy && selectedTeamMember?.name && (
                      <div className="contract-preview__party-represented">
                        Vertreten durch: {selectedTeamMember.name}
                      </div>
                    )}
                    {selectedTeamMember?.billingStreet && (
                      <div className="contract-preview__party-address">{selectedTeamMember.billingStreet}</div>
                    )}
                    {selectedTeamMember?.billingZip && selectedTeamMember?.billingCity && (
                      <div className="contract-preview__party-address">{selectedTeamMember.billingZip} {selectedTeamMember.billingCity}</div>
                    )}
                    {selectedTeamMember?.billingCountry && (
                      <div className="contract-preview__party-address">{selectedTeamMember.billingCountry}</div>
                    )}
                    <div className="contract-preview__party-label">
                      - Nachstehend als <strong>Anbieter</strong> bezeichnet
                    </div>
                  </div>
                </div>

                {/* Separator */}
                <div className="contract-preview__separator">
                  Und
                </div>

                {/* Auftraggeber (Client) */}
                <div className="contract-preview__party contract-preview__party--client">
                  <div className="contract-preview__party-content">
                    <div className="contract-preview__party-name">
                      {contractData.customerName || 'Customer Name'}
                    </div>
                    {contractData.customerRepresentedBy && (
                      <div className="contract-preview__party-represented">
                        Vertreten durch: {contractData.customerRepresentedBy}
                      </div>
                    )}
                    {contractData.customerStreet && (
                      <div className="contract-preview__party-address">{contractData.customerStreet}</div>
                    )}
                    {contractData.customerZip && contractData.customerCity && (
                      <div className="contract-preview__party-address">{contractData.customerZip} {contractData.customerCity}</div>
                    )}
                    {contractData.customerCountry && (
                      <div className="contract-preview__party-address">{contractData.customerCountry}</div>
                    )}
                    <div className="contract-preview__party-label">
                      - Nachstehend als <strong>Auftraggeber</strong> bezeichnet
                    </div>
                  </div>
                </div>
              </div>
            
              {selectedTemplate.paragraphs.map((para, index) => (
                <div key={index} className="contract-preview__paragraph">
                  <h3 className="contract-preview__paragraph-title">
                    §{para.order || index + 1} {para.title}
                  </h3>
                  <div className="contract-preview__paragraph-content">
                    {replaceVariables(para.text, contractData.variableValues, contractData.language)}
                  </div>
                </div>
              ))}

              {/* Additional Text */}
              {contractData.contractAdditionalText && (
                <div className="contract-preview__additional-text">
                  {contractData.contractAdditionalText}
                </div>
              )}

              {/* Signatures in preview */}
              <div className="contract-preview__signatures">
                <div className="contract-preview__signature contract-preview__signature--provider">
                  <div className="contract-preview__signature-box">
                    {selectedTeamMember?.signature ? (
                      <img 
                        src={selectedTeamMember.signature} 
                        alt="Our signature" 
                        className="contract-preview__signature-image"
                        style={{ maxWidth: `${selectedTeamMember.signatureMaxWidth || 150}px`, maxHeight: '100px' }}
                      />
                    ) : (
                      <div className="contract-preview__signature-placeholder">Our Signature</div>
                    )}
                  </div>
                  <div className="contract-preview__signature-name">
                    {selectedTeamMember?.companyName || 'Our Company'}
                  </div>
                  <div className="contract-preview__signature-city">
                    {selectedTeamMember?.billingCity || ''}
                  </div>
                </div>
                <div className="contract-preview__signature contract-preview__signature--client">
                  <div className="contract-preview__signature-box">
                    {contractData.customerSignature ? (
                      <img 
                        src={contractData.customerSignature} 
                        alt="Customer signature" 
                        className="contract-preview__signature-image"
                        style={{ maxWidth: '200px', maxHeight: '100px' }}
                      />
                    ) : null}
                  </div>
                  <div className="contract-preview__signature-name">
                    {contractData.customerName || 'Customer Name'}
                  </div>
                  <div className="contract-preview__signature-city">
                    {contractData.customerCity || ''}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save and Export Buttons */}
      <div className="offer-maker__button-group" style={{ marginTop: '2rem' }}>
        <button 
          className="offer-maker__save-btn" 
          onClick={handleSave}
        >
          {contractId ? 'Update' : 'Save'} Contract
          <span className="offer-maker__save-icon">💾</span>
        </button>
        {selectedTemplate && (
          <button 
            className="offer-maker__save-btn" 
            onClick={handleExport}
            style={{ backgroundColor: '#00aa55', marginLeft: '10px' }}
          >
            Export Contract
            <span className="offer-maker__save-icon">📄</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ContractMaker;

